// covered-by: frontends/carrie/src/routes/ask-puntt/utils/__tests__/commentMemoryCitations.test.tsx

/**
 * How a review comment's body draws the saved memories it rests on.
 *
 * Kept out of `AssetViewerPane.tsx` (past 5,000 lines): the pane hands its
 * citations to `useCommentMemoryCitations` and spreads `memoryMarkdownProps` on
 * each body, and everything it would otherwise decide itself is decided here.
 */

import { useMemo } from "react";

import {
  type LinkableMemory,
  memoryLinksFor,
} from "../../../utils/memoryCitationFields";

/**
 * The memories of one comment, numbered over its own text, for the BODY.
 *
 * why `chip: false`: in the comment panel the chip rides in the citation row
 * below the body (`CommentCitationLinks`), so the body only tints the sentence a
 * memory supports. Both read the same citations and the same description, so a
 * memory has one number in both places.
 */
export function useCommentMemoryCitations(
  citations: readonly unknown[],
  description: string | undefined,
  commentId: string | undefined,
): LinkableMemory[] {
  return useMemo(
    () =>
      memoryLinksFor(citations, description ?? "", commentId ?? "").map(
        (memory) => ({ ...memory, chip: false }),
      ),
    [citations, description, commentId],
  );
}

/**
 * What a comment body hands `Markdown` for its memories.
 *
 * why the paragraph fallback only for a body drawn whole: a comment split into
 * sections renders each section as its own body, and a fallback in each would
 * place the same chip once per section.
 */
export function memoryMarkdownProps(
  memoryCitations: LinkableMemory[] | undefined,
  sectioned: boolean,
): { memoryCitations?: LinkableMemory[]; memoryCitationsFallback: boolean } {
  return { memoryCitations, memoryCitationsFallback: !sectioned };
}
